import { useEffect, useMemo, useState } from 'react';
import { photosApi } from '../api/photos.api';
import type { Trip, TripPhoto } from '../types';
import { hidesTab } from '../utils/tripPermissions';

/**
 * The trip's photos grouped by the stop they belong to (ADR-0042), for the
 * itinerary. Agency staff never load them: the gallery is the travellers' (ADR-0038).
 */
export function useStopPhotos(trip: Pick<Trip, 'id' | 'viewerIsAgency'>) {
  const [photos, setPhotos] = useState<TripPhoto[]>([]);
  const hidden = hidesTab(trip, 'gallery');

  useEffect(() => {
    if (hidden) return;
    photosApi.list(trip.id).then(setPhotos).catch(() => setPhotos([]));
  }, [trip.id, hidden]);

  const byStop = useMemo(() => {
    const map = new Map<string, TripPhoto[]>();
    for (const p of photos) {
      if (!p.activityId) continue;
      map.set(p.activityId, [...(map.get(p.activityId) ?? []), p]);
    }
    return map;
  }, [photos]);

  return {
    photosOf: (activityId: string) => byStop.get(activityId) ?? [],
    replace: (updated: TripPhoto) => setPhotos(prev => prev.map(p => p.id === updated.id ? updated : p)),
    remove: (photoId: string) => setPhotos(prev => prev.filter(p => p.id !== photoId)),
  };
}
